import { Trans, useTranslation } from 'react-i18next'
import { usePrevious } from 'react-use'
import clsx from 'clsx'
import tinycolor from 'tinycolor2'

import { Edit16, Moon24, Sun24, SunMoon24 } from '@makyoapp/makyo-icon/icon'
import {
  AppIconDropdown,
  ColorPickerV2,
  colorToStyle,
  Field,
  JustifyVariant,
  RenderFileOrIcon,
  SimpleInput,
  SingleIconSelect,
  TextArea,
} from '@makyoapp/makyo-ui'

import { appConfig } from '@/configs'
import { AppProjectState, useAppContext } from '@/context/AppContext'
import { useCacheContext } from '@/context/CacheContext'
import { AccordionItem } from '@/components/Accordion/Accordion'
import { SaveButton } from '@/components/SaveButton'

import useInfoActionAccordion from '../../hooks/components/useInfoActionAccordion'

function getImageUrl(iconUrl: string | undefined, hasChanged: boolean, withCache: boolean): string | undefined {
  const version = hasChanged ? new Date().getTime() : null
  return iconUrl && iconUrl.startsWith('http') && withCache && version ? `${iconUrl}?v=${version}` : iconUrl
}

function hasProjectChanged(currentProject: AppProjectState | undefined, previousProject: AppProjectState): boolean {
  return JSON.stringify(currentProject) !== JSON.stringify(previousProject)
}

function InfoAccordionItem() {
  const { t } = useTranslation()
  const {
    emoji,
    loadingUpload,
    uploadError,
    baseColor,
    project,
    isUrl,
    titleError,
    tempAuthor,
    setTempAuthor,
    tempDesc,
    setTempDesc,
    tempTitle,
    file,
    previewName,
    maxFileSizeDisplay,
    colorScheme,
    handleSelectEmoji,
    handleUploadAppIcon,
    handleRemoveAppIcon,
    handleChangeAppName,
    handleChangeIconBg,
    handleSelectColorScheme,
    handleSaveAppInfo,
    loadingSubmit,
    submitButtonEnabled,
  } = useInfoActionAccordion()

  const { project: projectApp } = useAppContext()

  const { imageCache } = useCacheContext()
  const withCache = !!project && !!imageCache.values[project.id]

  const previousProject = usePrevious(projectApp)
  const hasChanged = previousProject ? hasProjectChanged(projectApp, previousProject) : true

  const fileSrc = getImageUrl(projectApp?.icon, hasChanged, withCache)

  return (
    <>
      <AccordionItem itemId='setting-logo' title={<Trans>Logo & Theme</Trans>}>
        <Field>
          {/* Label for the field */}
          <label className='w-full'>
            <Trans>App Icon</Trans>
          </label>

          {/* Dropdown for selecting or uploading app icon */}
          <AppIconDropdown
            onEmojiSelect={handleSelectEmoji}
            emoji={emoji}
            autoUpload
            loadingUpload={loadingUpload}
            uploadPreview={
              previewName && file?.src
                ? {
                    name: previewName,
                    src: file?.src,
                  }
                : undefined
            }
            onUploadChange={handleUploadAppIcon}
            onClear={handleRemoveAppIcon}
            uploadError={uploadError}
            uploadDescription={`Max file size: ${maxFileSizeDisplay}. Accepts: PNG, GIF, JPG, SVG.`}
          >
            {/* Preview area for the app icon */}
            <div
              className={clsx(
                'preview w-20 h-20',
                'rounded-xl border items-center justify-center flex shrink-0 overflow-hidden',
                'relative'
              )}
              style={{
                // Background color of the preview area, determined by the project's icon background color or a base color
                background: colorToStyle(tinycolor(project?.iconBgColor || baseColor).toRgb()),
              }}
            >
              {/* Render the file or icon */}
              <RenderFileOrIcon
                fileSrc={isUrl ? fileSrc : undefined} // Source of the file to be rendered, if any
                emoji={!isUrl ? emoji : undefined} // Emoji to be rendered, if any
                className='w-full h-full object-cover'
              />
              {/* Overlay for the preview area */}
              <div className='preview-overlay'>
                <Edit16 /> {/* Edit icon */}
              </div>
            </div>
          </AppIconDropdown>
        </Field>

        <Field>
          {/* Label for the field */}
          <label className='w-full'>
            <Trans>Icon Background Color</Trans>
          </label>
          {/* Color Picker Component for choosing Icon Background Color */}
          <ColorPickerV2 color={baseColor} onSave={(color) => handleChangeIconBg(color.hex)} />
        </Field>
        <Field>
          {/* Label for the field */}
          <label className='w-full'>{t('Color Scheme')}</label>
          {/* Single Icon Selector Component for choosing color scheme */}
          <SingleIconSelect
            id='app-appearance-scheme'
            justify={JustifyVariant.Start}
            onClick={handleSelectColorScheme}
            selected={colorScheme.saved}
            options={[
              {
                icon: () => <Sun24 />,
                title: t('Light'),
                id: 'light',
              },
              {
                icon: () => <Moon24 />,
                title: t('Dark'),
                id: 'dark',
              },
              {
                icon: () => <SunMoon24 />,
                title: t("Match device's setting"),
                id: 'match-device',
              },
            ]}
          />
        </Field>
      </AccordionItem>

      <AccordionItem itemId='setting-info' title={<Trans>Info</Trans>}>
        {/* Field for App Name */}
        <Field isVertical>
          <label>
            <Trans>App Name</Trans>
          </label>
          {/* Input for App Name */}
          <SimpleInput
            type='text'
            withCounterConfig={appConfig.charConstraints} // Character constraints for the input
            value={tempTitle} // Currently entered title
            error={titleError} // Error state for the title
            onChange={handleChangeAppName} // Function to handle change in title
          />
        </Field>
        {/* Field for Author */}
        <Field isVertical>
          <label>
            <Trans>Author</Trans>
          </label>
          {/* Input for Author */}
          <SimpleInput
            value={tempAuthor} // Currently entered author
            onChange={(e) => {
              setTempAuthor(e.currentTarget.value) // Function to set the author when it changes
            }}
          />
        </Field>
        {/* Field for Description */}
        <Field isVertical>
          <label>
            <Trans>Description</Trans>
          </label>
          {/* Text Area for Description */}
          <TextArea
            value={tempDesc} // Currently entered description
            onChange={(e) => {
              setTempDesc(e.currentTarget.value) // Function to set the description when it changes
            }}
          />
        </Field>

        <div className='mt-4 flex justify-end'>
          <SaveButton isLoading={loadingSubmit} disabled={!submitButtonEnabled} onClick={handleSaveAppInfo} />
        </div>
      </AccordionItem>
    </>
  )
}

export default InfoAccordionItem
